<script setup lang="ts">
  import { computed, onMounted, ref } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import moment from "moment";
  import { useAdTemplatesStore } from "@stores/adTemplates";
  import { useToastsStore } from "@stores/toastsStore";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { IndexTable } from "@/components/episto-ui/index-table";
  import type { DataTableColumn } from "@/components/episto-ui/data-table";
  import { FilterDropdown } from "@/components/episto-ui/filter-dropdown";
  import { createFilters } from "@/utils/composables/filters/createFilters";
  import { FormDialog } from "@/components/episto-ui/form-dialog";
  import { Badge } from "@/components/ui/badge";
  import { Input } from "@/components/ui/input";
  import { useRowNavigation } from "@/composables/useRowNavigation";
  import { serverErrorDetail } from "@utils/serverErrorDetail";
  import type { AdTemplateSummary } from "@/types";

  const router = useRouter();
  const { openRow } = useRowNavigation();
  const toast = useToastsStore();
  const adTemplatesStore = useAdTemplatesStore();
  const { templates } = storeToRefs(adTemplatesStore);

  const searchQuery = ref("");
  const rendererFilter = ref<string[]>([]);
  const statusFilter = ref<string[]>(["active"]);

  const statusOf = (template: AdTemplateSummary) => (template.archived_at ? "archived" : "active");

  const rendererOptions = computed(() =>
    [
      { label: "HTML", value: "html" },
      { label: "Bannerbear", value: "bannerbear" },
    ].map((option) => ({
      ...option,
      count: templates.value.filter((template) => template.renderer === option.value).length,
    })),
  );

  const statusOptions = computed(() =>
    [
      { label: "Active", value: "active" },
      { label: "Archived", value: "archived" },
    ].map((option) => ({
      ...option,
      count: templates.value.filter((template) => statusOf(template) === option.value).length,
    })),
  );

  const resetFilters = () => {
    rendererFilter.value = [];
    statusFilter.value = ["active"];
  };

  const { filterConfigs, activeFiltersCount, handleFilterChange, handleClear } = createFilters({
    clear: resetFilters,
    filters: [
      { id: "renderer", label: "Renderer", options: rendererOptions, model: rendererFilter },
      { id: "status", label: "Status", options: statusOptions, model: statusFilter },
    ],
  });
  const isDialogOpen = ref(false);
  const newName = ref("");
  const creating = ref(false);

  const filteredTemplates = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    return templates.value.filter(
      (template) =>
        (!query || template.name.toLowerCase().includes(query)) &&
        (!rendererFilter.value.length || rendererFilter.value.includes(template.renderer)) &&
        (!statusFilter.value.length || statusFilter.value.includes(statusOf(template))),
    );
  });

  const columns: DataTableColumn<AdTemplateSummary>[] = [
    { key: "preview_url", label: "Preview", class: "w-20" },
    { key: "name", label: "Name", class: "font-medium" },
    { key: "renderer", label: "Renderer" },
    { key: "published", label: "Published" },
    { key: "drafts_count", label: "Drafts" },
    { key: "updated_at", label: "Updated" },
  ];

  const openEditor = (template: AdTemplateSummary, event?: MouseEvent | KeyboardEvent) =>
    openRow({ name: "AdTemplateEditor", params: { token: template.token } }, event);

  const createTemplate = async () => {
    creating.value = true;
    try {
      const template = await adTemplatesStore.createTemplate(newName.value.trim());
      isDialogOpen.value = false;
      newName.value = "";
      router.push({ name: "AdTemplateEditor", params: { token: template.token } });
    } catch (error) {
      toast.error("Failed to create the template", serverErrorDetail(error));
    } finally {
      creating.value = false;
    }
  };

  onMounted(() => adTemplatesStore.fetchTemplates());
</script>

<template>
  <IndexPageLayout
    title="Ad templates"
    action-label="New template"
    @action="isDialogOpen = true">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search templates...">
        <template #filters>
          <FilterDropdown
            :filters="filterConfigs"
            :active-filters-count="activeFiltersCount"
            :matching-count="filteredTemplates.length"
            matching-label="templates"
            @toggle="(filterId, value) => handleFilterChange(filterId, value)"
            @clear="handleClear" />
        </template>
      </IndexPageFilters>
    </template>

    <IndexTable
      :items="filteredTemplates"
      :columns="columns"
      item-key="token"
      empty-message="No template found."
      @row-click="openEditor">
      <template #cell-preview_url="{ item }">
        <img
          v-if="item.preview_url?.startsWith('http')"
          :src="item.preview_url"
          :alt="item.name"
          class="size-12 rounded-sm object-cover" />
        <div
          v-else
          class="size-12 rounded-sm bg-muted" />
      </template>
      <template #cell-name="{ item }">
        <span class="flex items-center gap-2">
          {{ item.name }}
          <Badge
            v-if="item.archived_at"
            variant="secondary">
            Archived
          </Badge>
        </span>
      </template>
      <template #cell-renderer="{ item }">
        <Badge :variant="item.renderer === 'html' ? 'info' : 'secondary'">
          {{ item.renderer === "html" ? "HTML" : "Bannerbear" }}
        </Badge>
      </template>
      <template #cell-published="{ item }">
        <span v-if="item.published_version_number">v{{ item.published_version_number }}</span>
        <span
          v-else-if="item.renderer === 'bannerbear'"
          class="text-muted-foreground">
          Bannerbear
        </span>
        <span
          v-else
          class="text-muted-foreground">
          Not published
        </span>
      </template>
      <template #cell-drafts_count="{ item }">
        {{ item.drafts_count || "—" }}
      </template>
      <template #cell-updated_at="{ item }">
        {{ moment(item.updated_at).format("D MMM YYYY") }}
      </template>
    </IndexTable>

    <template #dialogs>
      <FormDialog
        :open="isDialogOpen"
        title="New ad template"
        confirm-label="Create"
        :loading="creating"
        :can-save="newName.trim().length > 0"
        @update:open="isDialogOpen = $event"
        @save="createTemplate">
        <Input
          v-model="newName"
          placeholder="e.g. T8 — city question"
          class="text-sm"
          @keydown.enter="newName.trim() && createTemplate()" />
        <p class="text-xs text-muted-foreground">
          The template starts from a background, a title and a CTA. It stays hidden from campaigns until you publish
          it.
        </p>
      </FormDialog>
    </template>
  </IndexPageLayout>
</template>
